import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Download, FileJson, ChevronLeft, ChevronRight, X, Hexagon, Lock, Sparkles, Bell, Save } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BrutalButton, BentoCard } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";
import { PropertyDrawer } from "@/components/dashboard/PropertyDrawer";
import { PROPERTIES, ALERTS } from "@/constants/testIds";

const emptyFilters = {
  q: "",
  state: "",
  city: "",
  jurisdiction: "",
  status: "",
  source_id: "",
  min_amount: "",
  max_amount: "",
};

export default function PropertiesPage() {
  const { isSubscribed, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => {
    const src = searchParams.get("source_id");
    return src ? { ...emptyFilters, source_id: src } : emptyFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pending, setPending] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [drawer, setDrawer] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const queryParams = useMemo(() => {
    const params = { page, page_size: pageSize };
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    return params;
  }, [applied, page, pageSize]);

  const { data, isFetching } = useQuery({
    queryKey: ["properties", queryParams],
    queryFn: async () => (await api.get("/properties", { params: queryParams })).data,
    keepPreviousData: true,
  });

  // Full nationwide whitelist for the "state" gate (250+ cities).
  const { data: allCities } = useQuery({
    queryKey: ["cities"],
    queryFn: async () => (await api.get("/cities")).data,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const facets = data?.facets || { states: [], cities: [], jurisdictions: [], statuses: [], sources: [] };
  const whitelistCities = data?.whitelist_cities || [];
  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Compute the option list for the City dropdown:
  //  * If a state is chosen (pending), use its top-5 whitelist from
  //    the full nationwide map so options update instantly, before Apply.
  //  * Otherwise flatten every state's top-5 list.
  const cityOptions = React.useMemo(() => {
    if (pending.state && allCities?.by_state) return allCities.by_state[pending.state] || [];
    if (pending.state && whitelistCities.length) return whitelistCities;
    if (allCities?.by_state) {
      const all = new Set();
      Object.values(allCities.by_state).forEach((arr) => arr.forEach((c) => all.add(c)));
      return Array.from(all).sort();
    }
    return facets.cities || [];
  }, [pending.state, whitelistCities, allCities, facets.cities]);

  const applyFilters = () => {
    setApplied(pending);
    setPage(1);
  };
  const clearFilters = () => {
    setPending(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  const exportUrl = (fmt) => {
    const params = new URLSearchParams({ fmt });
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params.append(k, v);
    });
    return `${API_BASE}/export?${params.toString()}`;
  };

  return (
    <div data-testid={PROPERTIES.root} className="space-y-6">
      {data?.locked ? (
        <div
          data-testid="paywall-banner"
          className="flex items-start md:items-center gap-4 border-2 border-red-600 bg-red-50 px-5 py-4 shadow-brutal-sm"
        >
          <Lock className="text-red-600 mt-0.5 md:mt-0" size={22} strokeWidth={2.5} />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-black">
              You're seeing a masked preview.
            </div>
            <p className="text-sm text-zinc-700 mt-0.5">
              Owner names, parcel numbers, and exact amounts are blurred for
              non-subscribers. Start your <strong>3-day free trial</strong> to
              unlock every field on every listing nationwide.
            </p>
          </div>
          <Link
            data-testid="paywall-cta"
            to="/pricing"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-red-600 text-white font-display font-semibold text-sm shadow-brutal-sm hover:bg-red-500"
          >
            <Sparkles size={14} strokeWidth={2.5} /> {isAuthenticated ? "Start trial" : "Sign in"}
          </Link>
        </div>
      ) : null}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="data-label">Ledger</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Properties</h1>
          <p className="text-sm text-zinc-600 mt-2">
            {total.toLocaleString()} record{total === 1 ? "" : "s"} match{total === 1 ? "es" : ""} your filter.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isAuthenticated ? (
            <button
              data-testid={ALERTS.saveBtn}
              onClick={() => setSaveModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-yellow-400 text-black font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300"
              title="Save this filter combo as an email alert"
            >
              <Bell size={16} strokeWidth={2.5} />
              Save Search
            </button>
          ) : null}
          {isSubscribed ? (
            <>
              <a
                data-testid={PROPERTIES.exportCsvBtn}
                href={exportUrl("csv")}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-white text-black font-display font-semibold text-sm shadow-brutal-sm hover:bg-zinc-50"
              >
                <Download size={16} strokeWidth={2.5} />
                Export CSV
              </a>
              <a
                data-testid={PROPERTIES.exportJsonBtn}
                href={exportUrl("json")}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-white text-black font-display font-semibold text-sm shadow-brutal-sm hover:bg-zinc-50"
              >
                <FileJson size={16} strokeWidth={2.5} />
                Export JSON
              </a>
            </>
          ) : (
            <Link
              to="/pricing"
              data-testid="properties-export-locked"
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-black bg-zinc-100 text-zinc-500 font-display font-semibold text-sm"
              title="Subscribe to export"
            >
              <Lock size={14} /> Export locked
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <BentoCard className="!p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <SearchInput
            testid={PROPERTIES.searchInput}
            value={pending.q}
            onChange={(v) => setPending((p) => ({ ...p, q: v }))}
          />
          <SelectField
            testid={PROPERTIES.stateSelect}
            label="State"
            value={pending.state}
            options={facets.states}
            onChange={(v) => setPending((p) => ({ ...p, state: v, city: "" }))}
          />
          <SelectField
            testid={PROPERTIES.citySelect}
            label={`City ${pending.state ? "(top 5)" : "(nationwide)"}`}
            value={pending.city}
            options={cityOptions}
            onChange={(v) => setPending((p) => ({ ...p, city: v }))}
          />
          <SelectField
            testid={PROPERTIES.jurisdictionSelect}
            label="Jurisdiction"
            value={pending.jurisdiction}
            options={facets.jurisdictions}
            onChange={(v) => setPending((p) => ({ ...p, jurisdiction: v }))}
          />
          <SelectField
            testid={PROPERTIES.statusSelect}
            label="Status"
            value={pending.status}
            options={facets.statuses}
            onChange={(v) => setPending((p) => ({ ...p, status: v }))}
          />
          <SelectField
            testid={PROPERTIES.sourceSelect}
            label="Source"
            value={pending.source_id}
            options={facets.sources}
            onChange={(v) => setPending((p) => ({ ...p, source_id: v }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              testid={PROPERTIES.minAmount}
              label="Min $"
              value={pending.min_amount}
              onChange={(v) => setPending((p) => ({ ...p, min_amount: v }))}
            />
            <NumberField
              testid={PROPERTIES.maxAmount}
              label="Max $"
              value={pending.max_amount}
              onChange={(v) => setPending((p) => ({ ...p, max_amount: v }))}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <BrutalButton data-testid={PROPERTIES.applyFiltersBtn} onClick={applyFilters}>
            Apply Filters
          </BrutalButton>
          <BrutalButton
            data-testid={PROPERTIES.clearFiltersBtn}
            variant="secondary"
            onClick={clearFilters}
          >
            Clear
          </BrutalButton>
        </div>
      </BentoCard>

      {/* Table */}
      <div className="bg-white border-2 border-black overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid={PROPERTIES.table} className="min-w-full text-sm">
            <thead className="bg-zinc-50 border-b-2 border-black">
              <tr>
                <Th>Score</Th>
                <Th>Parcel</Th>
                <Th>Owner</Th>
                <Th>Address</Th>
                <Th>City</Th>
                <Th>State</Th>
                <Th>Jurisdiction</Th>
                <Th>Sale Date</Th>
                <Th className="text-right">Amount</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !isFetching && (
                <tr>
                  <td colSpan={10} className="py-16 text-center">
                    <Hexagon size={40} className="mx-auto text-yellow-400" strokeWidth={1.5} />
                    <p className="mt-3 font-display font-semibold">No properties match.</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Trigger a scrape from the Dashboard or Sources page to fill the hive.
                    </p>
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={row.id}
                  data-testid={PROPERTIES.row(row.id)}
                  onClick={() => setDrawer(row)}
                  className={`border-b border-zinc-100 hover:bg-yellow-50 cursor-pointer transition-colors ${row.is_locked ? "" : ""}`}
                >
                  <Td><DealScoreBadge score={row.deal_score} /></Td>
                  <Td className={`font-mono-data font-semibold ${row.is_locked ? "blur-[3px] select-none" : ""}`}>{row.parcel_number || "—"}</Td>
                  <Td className={row.is_locked ? "blur-[3px] select-none" : ""}>{row.owner || "—"}</Td>
                  <Td className={row.is_locked ? "blur-[2px] select-none" : ""}>{row.address || "—"}</Td>
                  <Td className="font-semibold">{row.city || "—"}</Td>
                  <Td className="font-mono-data">{row.state || "—"}</Td>
                  <Td>{row.jurisdiction || "—"}</Td>
                  <Td className="font-mono-data">{row.sale_date || "—"}</Td>
                  <Td className="text-right font-mono-data font-semibold">
                    {row.is_locked
                      ? <span className="text-zinc-500">{row.amount_owed_bucket || "•••"}</span>
                      : fmtMoney(row.amount_owed)}
                  </Td>
                  <Td>
                    <StatusBadge value={row.status} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t-2 border-black bg-zinc-50">
          <div className="text-xs font-mono-data text-zinc-600">
            Page {page} of {totalPages} · {total.toLocaleString()} total
          </div>
          <div className="flex gap-2">
            <BrutalButton
              variant="secondary"
              data-testid={PROPERTIES.pagePrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft size={14} /> Prev
            </BrutalButton>
            <BrutalButton
              variant="secondary"
              data-testid={PROPERTIES.pageNext}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next <ChevronRight size={14} />
            </BrutalButton>
          </div>
        </div>
      </div>

      {drawer ? <PropertyDrawer row={drawer} onClose={() => setDrawer(null)} /> : null}
      {saveModalOpen ? (
        <SaveSearchModal
          filters={pending}
          onClose={() => setSaveModalOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left px-4 py-3 text-[10px] font-mono-data font-bold uppercase tracking-widest text-zinc-600 ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function StatusBadge({ value }) {
  if (!value) return <HexBadge>—</HexBadge>;
  const v = value.toLowerCase();
  const tone = v.includes("redeem")
    ? "success"
    : v.includes("scheduled") || v.includes("active")
    ? "running"
    : v.includes("sold") || v.includes("cancel")
    ? "error"
    : "default";
  return <HexBadge tone={tone}>{value}</HexBadge>;
}

function DealScoreBadge({ score }) {
  if (!score) return <span className="text-zinc-400">—</span>;
  const s = score.score;
  const bg =
    s >= 80 ? "bg-red-600 text-white" :
    s >= 65 ? "bg-yellow-400 text-black" :
    s >= 40 ? "bg-zinc-200 text-zinc-700" :
    "bg-zinc-100 text-zinc-500";
  return (
    <div className="inline-flex items-center gap-2">
      <div className={`w-11 h-11 clip-hex flex items-center justify-center border-2 border-black font-display font-bold text-sm ${bg}`}>
        {s}
      </div>
      <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
        {score.label}
      </span>
    </div>
  );
}

function SearchInput({ value, onChange, testid }) {
  return (
    <div className="lg:col-span-2">
      <label className="data-label block mb-1.5">Search</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          data-testid={testid}
          type="text"
          placeholder="Owner, parcel #, address…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="data-label block mb-1.5">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
      >
        <option value="">All</option>
        {(options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="data-label block mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm font-mono-data"
      />
    </div>
  );
}

function SaveSearchModal({ filters, onClose }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(defaultSearchName(filters));
  const [cadence, setCadence] = useState("both");

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim() || "Saved search",
        cadence,
        filters: cleanFilters(filters),
      };
      return (await api.post("/alerts/searches", payload)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", "searches"] });
      toast.success("Alert saved. We'll email you the moment a match lands.");
      onClose();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save alert");
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      data-testid={ALERTS.saveModal}
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white border-2 border-black shadow-brutal">
        <div className="flex items-start justify-between px-6 py-4 border-b-2 border-black">
          <div>
            <div className="data-label">Save Search</div>
            <div className="font-display font-bold text-2xl tracking-tight mt-1">
              Get emailed when new matches land
            </div>
          </div>
          <button
            data-testid={ALERTS.saveModalCancel}
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 border-2 border-transparent hover:border-black"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="data-label block mb-1.5">Name this alert</label>
            <input
              data-testid={ALERTS.saveModalName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
              placeholder="e.g. Nashville under $10K"
            />
          </div>
          <div>
            <label className="data-label block mb-1.5">Cadence</label>
            <select
              data-testid={ALERTS.saveModalCadence}
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              className="w-full px-3 py-2 border-2 border-zinc-200 focus:border-yellow-500 focus:outline-none bg-white text-sm"
            >
              <option value="realtime">Real-time (email as soon as we see a match)</option>
              <option value="daily">Daily digest (once per day, batched)</option>
              <option value="both">Both — real-time and daily digest</option>
            </select>
          </div>
          <div className="border-2 border-yellow-300 bg-yellow-50 p-3">
            <div className="data-label mb-1">Filters being saved</div>
            <div className="text-xs font-mono-data text-zinc-700 break-words">
              {formatFilterPreview(filters)}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t-2 border-black bg-zinc-50">
          <BrutalButton variant="secondary" onClick={onClose} data-testid={ALERTS.saveModalCancel + "-btn"}>
            Cancel
          </BrutalButton>
          <BrutalButton
            data-testid={ALERTS.saveModalSubmit}
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !name.trim()}
          >
            <Save size={14} /> {createMut.isPending ? "Saving…" : "Save alert"}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}

function defaultSearchName(filters) {
  const parts = [];
  if (filters.city) parts.push(filters.city);
  else if (filters.state) parts.push(filters.state);
  if (filters.max_amount) parts.push(`under $${filters.max_amount}`);
  if (filters.status) parts.push(filters.status);
  if (parts.length === 0 && filters.q) parts.push(filters.q);
  return parts.length ? parts.join(" · ") : "New alert";
}

function cleanFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) out[k] = v;
  });
  return out;
}

function formatFilterPreview(filters) {
  const cleaned = cleanFilters(filters);
  const entries = Object.entries(cleaned);
  if (entries.length === 0) return "No filters — you'll be emailed for every new listing.";
  return entries.map(([k, v]) => `${k}: ${v}`).join(" · ");
}

